import { useRef, useState, useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

function useInView(ref: React.RefObject<HTMLElement | null>) {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setInView(true);
      },
      { threshold: 0.1 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [ref]);
  return inView;
}

export default function PrivacyPolicyPage() {
  const contentRef = useRef<HTMLElement>(null);
  const inView = useInView(contentRef);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <main>
        {/* Hero Section - Matching all other pages */}
        <section className="relative pt-32 pb-16 bg-white overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-teal-50/80 blur-3xl -translate-y-1/4 translate-x-1/4" />
          </div>
          <div className="relative max-w-7xl mx-auto px-6 lg:px-10 text-center">
            <h1 className="text-5xl lg:text-6xl font-extrabold text-gray-900 leading-tight mb-5">
              Privacy <span className="text-teal-500">Policy</span>
            </h1>
            <p className="text-gray-500 text-xl max-w-2xl mx-auto">
              Last updated: 16.09.2026
            </p>
          </div>
        </section>

        {/* Content Section */}
        <section ref={contentRef} className="pb-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div
              className={`space-y-10 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
            >
              {/* Introduction */}
              <div className="bg-gray-50 border border-gray-100 rounded-2xl p-8">
                <p className="text-gray-600 text-base leading-relaxed mb-4">
                  Elation SoftNet Pvt. Ltd. operates its website and provides software development, mobile applications, web applications, and technology-related services. This page informs you of our policies regarding the collection, use, and disclosure of Personal Information we receive from users of our website and services.
                </p>
                <p className="text-gray-600 text-base leading-relaxed">
                  We use your Personal Information only for providing, maintaining, improving, and supporting our services. By using our website or services, you agree to the collection and use of information in accordance with this Privacy Policy.
                </p>
              </div>

              {/* Information Collection and Use */}
              <div className="space-y-4">
                <h2 className="text-2xl font-bold text-gray-900">Information Collection and Use</h2>
                <p className="text-gray-600 leading-relaxed">
                  While using our website or services, we may ask you to provide certain personally identifiable information that can be used to contact or identify you.
                </p>
                <p className="text-gray-700 font-semibold text-sm">
                  Personally identifiable information may include, but is not limited to:
                </p>
                <div className="grid sm:grid-cols-2 gap-3 pt-1">
                  {[
                    'Name',
                    'Email address',
                    'Phone number',
                    'Postal or business address',
                    'Company or organization details',
                    'Information submitted through contact forms',
                    'Other information voluntarily provided by you',
                  ].map((item) => (
                    <div
                      key={item}
                      className="flex items-start gap-2.5 bg-gray-50 border border-gray-100 rounded-xl px-4 py-3 text-sm text-gray-700"
                    >
                      <i className="ri-checkbox-circle-fill text-teal-600 text-base flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
                <p className="text-gray-600 leading-relaxed pt-2">
                  We use this information to respond to inquiries, provide requested services, communicate with users, and improve our products and services.
                </p>
              </div>

              {/* Log Data */}
              <div className="space-y-4">
                <h2 className="text-2xl font-bold text-gray-900">Log Data</h2>
                <p className="text-gray-600 leading-relaxed">
                  Like many site operators, we collect information that your browser sends whenever you visit our website (&quot;Log Data&quot;).
                </p>
                <p className="text-gray-600 leading-relaxed">
                  This Log Data may include information such as your computer&apos;s Internet Protocol (&quot;IP&quot;) address, browser type, browser version, pages of our website that you visit, the time and date of your visit, the time spent on those pages, and other statistics.
                </p>

                {/* Identical 3 Cards */}
                <div className="grid sm:grid-cols-3 gap-4 pt-2">
                  {[
                    {
                      icon: 'ri-router-line',
                      title: 'IP Address',
                      desc: 'Geo-routing & security',
                    },
                    {
                      icon: 'ri-window-line',
                      title: 'Browser & Version',
                      desc: 'Layout compatibility',
                    },
                    {
                      icon: 'ri-time-line',
                      title: 'Session Timing',
                      desc: 'Platform performance',
                    },
                  ].map((item) => (
                    <div
                      key={item.title}
                      className="bg-gray-50 border border-gray-100 rounded-2xl p-5 text-center flex flex-col items-center justify-center hover:border-teal-200 transition-colors"
                    >
                      <div className="w-10 h-10 flex items-center justify-center bg-teal-100 text-teal-600 rounded-xl mb-3">
                        <i className={`${item.icon} text-xl`} />
                      </div>
                      <span className="font-bold text-gray-900 text-sm block mb-1">{item.title}</span>
                      <span className="text-gray-500 text-xs">{item.desc}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Cookies */}
              <div className="space-y-4">
                <h2 className="text-2xl font-bold text-gray-900">Cookies</h2>
                <p className="text-gray-600 leading-relaxed">
                  Cookies are files with a small amount of data, which may include an anonymous unique identifier. Cookies are sent to your browser from a website and stored on your device.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  Like many websites, we may use cookies and similar technologies to collect information, understand website usage, improve user experience, and maintain website functionality.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, if you do not accept cookies, some portions of our website may not function properly.
                </p>
              </div>

              {/* Use of Personal Information */}
              <div className="space-y-4">
                <h2 className="text-2xl font-bold text-gray-900">Use of Personal Information</h2>
                <p className="text-gray-600 leading-relaxed">
                  We may use the information collected for purposes including:
                </p>
                <div className="grid sm:grid-cols-2 gap-3">
                  {[
                    'Providing and maintaining our website and services',
                    'Responding to inquiries and requests',
                    'Providing customer and technical support',
                    'Improving our website, applications, and services',
                    'Communicating updates and service-related information',
                    'Understanding how users interact with our website',
                    'Maintaining security and preventing unauthorized activities',
                    'Complying with applicable legal requirements',
                  ].map((purpose) => (
                    <div
                      key={purpose}
                      className="flex items-start gap-2.5 bg-gray-50 border border-gray-100 rounded-xl px-4 py-3 text-sm text-gray-700"
                    >
                      <i className="ri-check-line text-teal-600 font-bold text-base flex-shrink-0 mt-0.5" />
                      <span>{purpose}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Information Security */}
              <div className="space-y-4">
                <h2 className="text-2xl font-bold text-gray-900">Information Security</h2>
                <p className="text-gray-600 leading-relaxed">
                  The security of your Personal Information is important to us. We take reasonable technical and organizational measures to protect Personal Information against unauthorized access, alteration, disclosure, or destruction.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  However, please be aware that no method of transmission over the Internet or method of electronic storage is completely secure.
                </p>
              </div>

              {/* Third-Party Services */}
              <div className="space-y-4">
                <h2 className="text-2xl font-bold text-gray-900">Third-Party Services</h2>
                <p className="text-gray-600 leading-relaxed">
                  Our website and services may contain links to or use third-party services. These third parties may have their own privacy policies and terms of use.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  Elation SoftNet Pvt. Ltd. is not responsible for the privacy practices or content of third-party websites or services. We encourage you to review their respective privacy policies before providing personal information.
                </p>
              </div>

              {/* Changes to This Privacy Policy */}
              <div className="space-y-4">
                <h2 className="text-2xl font-bold text-gray-900">Changes to This Privacy Policy</h2>
                <p className="text-gray-600 leading-relaxed">
                  This Privacy Policy is effective as of 16.09.2026 and will remain in effect except with respect to any changes in its provisions in the future.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  We reserve the right to update or change our Privacy Policy at any time. Any changes will become effective immediately after being posted on this page.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  You should check this Privacy Policy periodically for any updates. Your continued use of our website or services after we post modifications to this Privacy Policy will constitute your acknowledgment of the modifications and your agreement to be bound by the updated Privacy Policy.
                </p>
              </div>

              {/* Contact Us */}
              <div className="space-y-5 pt-4">
                <h2 className="text-2xl font-bold text-gray-900">Contact Us</h2>
                <p className="text-gray-600 leading-relaxed">
                  If you have any questions about this Privacy Policy, please contact us at:
                </p>

                <div className="bg-gray-50 border border-gray-100 rounded-2xl p-8 space-y-5">
                  <div className="text-lg font-bold text-gray-900">Elation SoftNet Pvt. Ltd.</div>

                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-teal-100 text-teal-600 rounded-xl flex-shrink-0">
                      <i className="ri-mail-line text-lg" />
                    </div>
                    <div>
                      <div className="text-xs text-gray-400 font-semibold uppercase mb-0.5">Email</div>
                      <a
                        href="mailto:info@elationsoft.net"
                        className="text-sm font-semibold text-teal-600 hover:underline"
                      >
                        info@elationsoft.net
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-teal-100 text-teal-600 rounded-xl flex-shrink-0">
                      <i className="ri-map-pin-line text-lg" />
                    </div>
                    <div>
                      <div className="text-xs text-gray-400 font-semibold uppercase mb-0.5">Address</div>
                      <address className="not-italic text-sm text-gray-700 font-medium leading-relaxed">
                        401 Shukh Complex,<br />
                        Munsipuliya, Indira Nagar,<br />
                        Lucknow, Uttar Pradesh – 226016, India.
                      </address>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
